import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import useSearch from "hooks/useSearch";
import { useModals } from "ModalProvider";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { searchByProp } from "utils/search";
import EditPriceListView from "./EditPriceListView";
import { DELETE_PRICE_LISTS, FETCH_PRICE_LISTS } from "./graphql";

const PriceLists = () => {
  const { loading, error, data } = useQuery(FETCH_PRICE_LISTS);
  const [deletePriceLists, deletePriceListsRes] = useMutation(
    DELETE_PRICE_LISTS,
    {
      onCompleted() {
        Alert("success", "Price lists deleted successfully.");
        setSelectedIds([]);
      },
      onError(error) {
        Alert("error", parseError(error));
      },
      refetchQueries: [{ query: FETCH_PRICE_LISTS }],
      fetchPolicy: "network-only",
      awaitRefetchQueries: true,
    },
  );

  const modal = useModals();
  const [q, setQ] = useSearch();
  const [selectedIds, setSelectedIds] = useState([]);
  const navigate = useNavigate();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const res = data.priceLists.filter((i) => searchByProp(i, ["name"], q));

  function deleteSelection() {
    if (
      window.confirm(
        `Are you sure to delete ${selectedIds.length} price list${
          selectedIds.length > 1 ? "s" : ""
        }?`,
      )
    ) {
      console.log("Not implemented yet");
      deletePriceLists({
        variables: { ids: selectedIds },
      });
    }
  }

  return (
    <Page
      title="Price Lists"
      staticHeader
      className="h-screen"
      rightButtons={
        <div className="flex space-x-6">
          <a
            href={
              process.env.REACT_APP_SERVER_ADMIN_URL +
              "product/pricelist/?deleted_at__isnull=True"
            }
            target="_blank"
          >
            Admin
          </a>
        </div>
      }
    >
      <div className="p-6 flex flex-col flex-1 overflow-auto">
        <div className="flex items-center space-x-6">
          <div className="flex-1">
            <SearchBar
              initialQuery={q}
              onChange={setQ}
              autoFocus
              placeholder="Search by price list name."
            />
          </div>

          <Button
            onClick={() =>
              modal.present({
                title: "Create Price List",
                center: true,
                children: (
                  <EditPriceListView
                    complete={(priceList, created) => {
                      modal.hide();
                      if (created === "CREATED") {
                        navigate(priceList.id);
                      }
                    }}
                  />
                ),
              })
            }
          >
            + Create Price List
          </Button>
        </div>

        <div className="card px-0 py-0 flex-1 overflow-auto mt-6">
          <div>
            <table className="whitespace-nowrap">
              <thead>
                <tr className="sticky top-0 backdrop-blur bg-gray-50 bg-opacity-70 dark:bg-gray-800 dark:bg-opacity-90 z-20">
                  <th className="px-4 py-3" colSpan={2}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="opacity-70"
                        checked={selectedIds.length === res.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(res.map((i) => i.id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                      {selectedIds.length > 0 ? (
                        <div className="flex items-center space-x-3">
                          <div>
                            {selectedIds.length} price list
                            {selectedIds.length > 0 ? "s" : ""} selected
                          </div>
                          <Button
                            danger
                            onClick={deleteSelection}
                            loading={deletePriceListsRes.loading}
                          >
                            Delete
                          </Button>
                          <Button onClick={() => setSelectedIds([])}>
                            Clear
                          </Button>
                        </div>
                      ) : (
                        <div>Price List</div>
                      )}
                    </div>
                  </th>
                  <th className="text-right">Used in Customers</th>
                  <th className="text-right px-6">Created By</th>
                </tr>
              </thead>
              <tbody>
                {res.map((priceList) => (
                  <tr
                    key={priceList.id}
                    className="py-2 border-y border-gray-100 dark:border-gray-700"
                  >
                    <td className="w-1 pl-4 pr-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          className="opacity-70"
                          checked={selectedIds.includes(priceList.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds([...selectedIds, priceList.id]);
                            } else {
                              setSelectedIds(
                                selectedIds.filter((id) => id !== priceList.id),
                              );
                            }
                          }}
                        />
                        <span className="opacity-50">{priceList.company}</span>
                      </div>
                    </td>
                    <td className="w-full">
                      <Link to={priceList.id}>
                        {priceList.name} [{priceList.currency}]
                      </Link>
                    </td>
                    <td className="text-right">
                      {priceList.customers.length > 0
                        ? priceList.customers.length
                        : "-"}
                    </td>
                    <td className="text-right opacity-70 px-6">
                      {priceList.createdBy ? priceList.createdBy.email : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="opacity-70 mt-4 text-xs">
          <p>
            WIS pricelist is almost the same as the Odoo one. We can also sync
            pricelist to Odoo. However, we build this feature because Odoo does
            not provide enough flexibility.
          </p>
          <p>This pricelist will be applied to Order Scheduler plans.</p>
        </div>
      </div>
    </Page>
  );
};

export default PriceLists;
